import hasAuthAccess from '@anm/auth/helpers/hasAuthAccess';
import ButtonLoader, { ButtonLoaderProps } from '@anm/components/buttons/ButtonLoader';
import useEventPending from '@anm/hooks/useEventPending';
import { noop } from 'lodash/fp';
import { useCallback, FC, MouseEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { authActions, authSelectors } from '../../../store/modules/auth';

export type AnonSignUpButtonProps = ButtonLoaderProps & UseAnonSignUpProps;

type UseAnonSignUpProps = { href?: string; onSignUpDone?: () => void };

const useAnonSignUp = ({ href, onSignUpDone = noop }: UseAnonSignUpProps) => {
  const dispatch = useDispatch();
  const isPending = useSelector(authSelectors.selectSignUpAnonPending);

  const openPage = () => window.open(href, '_self');
  const statEventPending = useEventPending(isPending, href ? openPage : onSignUpDone);

  const handleClick = useCallback(
    (e: MouseEvent) => {
      if (hasAuthAccess() || isPending) return;

      e.preventDefault();
      dispatch(authActions.signupAnon());
      statEventPending();
    },
    [isPending, hasAuthAccess()]
  );

  return [{ isPending }, handleClick] as const;
};

const AnonSignUpButton: FC<AnonSignUpButtonProps> = ({ onClick = noop, onSignUpDone, ...buttonProps }) => {
  const [{ isPending }, signUp] = useAnonSignUp({ href: buttonProps.href, onSignUpDone });
  const handleClick = (e: MouseEvent) => {
    onClick();
    signUp(e);
  };

  return <ButtonLoader isPending={isPending} onClick={handleClick} {...buttonProps} />;
};

export default AnonSignUpButton;
